import { Badge, Button, Card, CardBody, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, TabContent, TabPane } from "reactstrap";
import sidebar from "../../utils/sidebar";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
import {
  IoAlarmOutline, IoAttachOutline, IoCalendarOutline, IoCheckmarkCircleOutline, IoCreateOutline,
  IoFlagOutline, IoOpenOutline, IoPersonOutline, IoReaderOutline, IoTrashOutline
} from "react-icons/io5";
import { useEffect, useRef, useState } from "react";
import OverviewTab from "./Tabs/Overview";
import Timeline from "../../components/Timeline";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { del, get, post } from "../../../../utils/helpers/api";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../../context/settings";
import ConfirmationModal from "../../../../components/Modals/Confirmation";
import { toast } from "react-toastify";
import {
  formatMinutes, personName, printPriority, printTaskStatus, priorityLabel, TASK_STAGES,
  TASK_STATUSES, taskStatusLabel
} from "../../utils/helpers";
import {
  Avatar, Fact, RecordLayout, RecordMain, RecordNav, RecordRail, RecordTabs, Stages, StatStrip,
  useScrollSpy
} from "../../components/Record";

const STATUS_TINTS = {
  not_started: { background: '#f4f4f6', color: '#6e6e73' },
  in_progress: { background: '#e6f4ff', color: '#3b82f6' },
  completed: { background: '#e9f9f0', color: '#10b981' },
  deferred: { background: '#fff3e6', color: '#f59e0b' },
  cancelled: { background: '#ffe6eb', color: '#ef4444' }
};

const PRIORITY_TINTS = {
  high: { background: '#ffe6eb', color: '#e2214b' },
  normal: { background: '#f0eeff', color: '#6C63FF' },
  low: { background: '#e6f4ff', color: '#1d7afc' }
};

/** A task is late when its due date has passed and it is still open. */
function isOverdue(task) {
  if (!task?.due_date || ['completed', 'cancelled'].includes(task.status)) return false;

  const due = new Date(task.due_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due < today;
}

function ViewTaskPage() {
  const { t } = useTranslation();
  const { formatDate } = useSettings();
  const NAV_SECTIONS = [
    { id: 'task-informations-card', label: t('crm.taskInformationsSection'), icon: <IoCheckmarkCircleOutline size={14} /> },
    { id: 'notes-card', label: t('common.notes'), icon: <IoReaderOutline size={14} /> },
    { id: 'attachments-card', label: t('attachments.title'), icon: <IoAttachOutline size={14} /> }
  ];
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [taskData, setTaskData] = useState(state?.taskData || {});
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { activeSection, scrollToSection } = useScrollSpy(
    NAV_SECTIONS.map(section => section.id),
    scrollContainerRef,
    activeTab === 'overview',
    taskData?.id
  );

  useEffect(() => {
    if (id) {
      getTask();
    }
  }, [id]);

  const getTask = async () => {
    const data = await get('/crm/tasks/view/' + id);
    if (data?.task) {
      setTaskData(data.task);
    }
  };

  const deleteTask = async () => {
    const data = await del('/crm/tasks/delete/' + id);
    if (data?.success) {
      toast.success(data.message || t('crm.taskDeleted'));
      navigate('/crm/tasks');
    }
  };

  const changeStatus = async (toStatus) => {
    const fromStatus = taskData?.status || 'not_started';
    if (fromStatus === toStatus) return;

    const data = await post('/crm/tasks/status/update/' + id, { from_status: fromStatus, to_status: toStatus });
    if (data?.success) {
      setTaskData(prev => ({ ...prev, status: toStatus }));
    }
  };

  const subject = taskData?.subject || t('crm.task');
  const status = taskData?.status || 'not_started';
  const overdue = isOverdue(taskData);
  const customer = taskData?.customer;
  const customerName = customer?.company_name || personName(customer);
  const relatedTo = customer?.id
    ? `/crm/customers/view/${customer.id}`
    : taskData?.lead_id ? `/crm/leads/view/${taskData.lead_id}` : null;
  const statusTint = STATUS_TINTS[status] ?? STATUS_TINTS.not_started;
  const priorityTint = PRIORITY_TINTS[taskData?.priority] ?? PRIORITY_TINTS.normal;
  const stages = TASK_STAGES.map(stage => ({ value: stage, label: taskStatusLabel(stage) }));

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          title={subject}
          onBack={() => navigate('/crm/tasks')}
        >
          <Dropdown isOpen={statusOpen} toggle={() => setStatusOpen(prev => !prev)} direction="down" size="sm">
            <DropdownToggle outline color="secondary" caret>
              {taskStatusLabel(status)}
            </DropdownToggle>
            <DropdownMenu end>
              {TASK_STATUSES.map(option => (
                <DropdownItem key={option} active={status === option} onClick={() => changeStatus(option)}>
                  {taskStatusLabel(option)}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
          {status !== 'completed' && (
            <Button
              size="sm"
              color="primary"
              className="d-flex align-items-center gap-1"
              onClick={() => changeStatus('completed')}
            >
              <IoCheckmarkCircleOutline size={14} />
              {t('crm.closeTask')}
            </Button>
          )}
          <Dropdown isOpen={dropdownOpen} toggle={() => setDropdownOpen(prev => !prev)} direction="down" size="sm">
            <DropdownToggle outline color="secondary">
              <PiDotsThreeOutlineVerticalFill />
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem onClick={() => navigate(`/crm/tasks/edit/${id}`, { state: { taskData } })}>
                <IoCreateOutline size={15} /> {t('common.edit')}
              </DropdownItem>
              <DropdownItem divider />
              <DropdownItem className="text-danger" onClick={() => setDeleteOpen(true)}>
                <IoTrashOutline size={15} /> {t('common.delete')}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </PageSubHeader>

        <div className="subheader-page-content scrollable" ref={scrollContainerRef}>
          <RecordLayout>
            <RecordRail>
              <Card>
                <CardBody className="text-center pb-3">
                  <Avatar fallback={<IoCheckmarkCircleOutline size={26} />} />

                  <h6 className="fw-bold mb-2 mt-3">{subject}</h6>
                  <div className="d-flex justify-content-center gap-1 flex-wrap">
                    {printTaskStatus(status)}
                    {printPriority(taskData?.priority)}
                    {overdue && <Badge color="danger">{t('common.overdue')}</Badge>}
                  </div>

                  <div className="d-flex justify-content-center gap-2 mt-3">
                    <Button
                      size="sm"
                      outline
                      color="secondary"
                      disabled={!relatedTo}
                      onClick={() => navigate(relatedTo)}
                    >
                      <IoOpenOutline size={14} />
                    </Button>
                    <Button
                      size="sm"
                      outline
                      color="secondary"
                      onClick={() => navigate(`/crm/tasks/edit/${id}`, { state: { taskData } })}
                    >
                      <IoCreateOutline size={14} />
                    </Button>
                  </div>
                </CardBody>

                <hr className="my-0" />

                <CardBody className="d-flex flex-column gap-2">
                  <Fact icon={<IoPersonOutline size={14} />} value={personName(taskData?.assigned_to)} />
                  <Fact icon={<IoOpenOutline size={14} />} value={customerName} />
                  <Fact
                    icon={<IoCalendarOutline size={14} />}
                    value={taskData?.due_date ? formatDate(taskData.due_date) : null}
                  />
                  <Fact icon={<IoAlarmOutline size={14} />} value={formatMinutes(taskData?.remind_before)} />
                  <Fact icon={<IoFlagOutline size={14} />} value={priorityLabel(taskData?.priority)} />
                </CardBody>
              </Card>

              {activeTab === 'overview' && (
                <RecordNav sections={NAV_SECTIONS} activeId={activeSection} onSelect={scrollToSection} />
              )}
            </RecordRail>

            <RecordMain>
              <StatStrip
                tiles={[
                  {
                    icon: <IoCheckmarkCircleOutline size={18} />,
                    background: statusTint.background,
                    color: statusTint.color,
                    value: taskStatusLabel(status),
                    label: t('common.status')
                  },
                  {
                    icon: <IoFlagOutline size={18} />,
                    background: priorityTint.background,
                    color: priorityTint.color,
                    value: priorityLabel(taskData?.priority) || '-',
                    label: t('crm.priority')
                  },
                  {
                    icon: <IoCalendarOutline size={18} />,
                    background: overdue ? '#ffe6eb' : '#fff3e6',
                    color: overdue ? '#e2214b' : '#fd7e14',
                    value: taskData?.due_date ? formatDate(taskData.due_date) : '-',
                    label: overdue ? t('common.overdue') : t('sales.dueDate')
                  },
                  {
                    icon: <IoPersonOutline size={18} />,
                    background: '#f0eeff',
                    color: '#6C63FF',
                    value: personName(taskData?.assigned_to) || '-',
                    label: t('crm.assignedTo')
                  }
                ]}
              >
                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="crm-record-label">{t('common.status')}</span>
                    <span className="fw-bold" style={{ fontSize: '0.8rem' }}>{taskStatusLabel(status)}</span>
                  </div>
                  <Stages stages={stages} current={status} color={statusTint.color} />
                </div>
              </StatStrip>

              <RecordTabs
                tabs={[
                  { id: 'overview', label: t('crm.overview') },
                  { id: 'timeline', label: t('crm.timeline') }
                ]}
                activeTab={activeTab}
                onChange={setActiveTab}
              />
              <TabContent activeTab={activeTab} className="border-0 p-0">
                <TabPane tabId="overview">
                  <OverviewTab taskData={taskData} />
                </TabPane>
                <TabPane tabId="timeline">
                  <Timeline subjectType="task" subjectId={id} />
                </TabPane>
              </TabContent>
            </RecordMain>
          </RecordLayout>
        </div>
      </div>

      <ConfirmationModal
        isOpen={deleteOpen}
        toggle={() => setDeleteOpen(false)}
        onConfirm={deleteTask}
        title={t('crm.deleteTask')}
        message={t('crm.deleteTaskConfirm')}
      />
    </SidebarLayout>
  );
}

export default ViewTaskPage;
